import { useState, useEffect } from 'react';
import { PageLayout } from '@/components/PageLayout';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { CalendarDays, Clock, Plus, ChevronLeft, ChevronRight, X, AlertCircle, Coffee } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const statusLabels = {
  scheduled: { text: 'Onaylandı', color: 'bg-green-100 text-green-800' },
  pending_approval: { text: 'Onay Bekliyor', color: 'bg-blue-100 text-blue-800' },
  completed: { text: 'Tamamlandı', color: 'bg-gray-100 text-gray-800' },
  cancelled: { text: 'İptal', color: 'bg-red-100 text-red-800' },
  rejected: { text: 'Reddedildi', color: 'bg-red-100 text-red-600' },
};

const DAY_NAMES_TR = ['Pazartesi', 'Salı', 'Çarşamba', 'Perşembe', 'Cuma', 'Cumartesi', 'Pazar'];

function generateTimeSlots(start, end, duration, lunchStart, lunchEnd) {
  const slots = [];
  if (!start || !end || !duration) return slots;
  const [sh, sm] = start.split(':').map(Number);
  const [eh, em] = end.split(':').map(Number);
  const startMin = sh * 60 + sm;
  const endMin = eh * 60 + em;

  let hasLunch = false;
  let lsMin = 0, leMin = 0;
  if (lunchStart && lunchEnd) {
    const [lsh, lsm] = lunchStart.split(':').map(Number);
    const [leh, lem] = lunchEnd.split(':').map(Number);
    lsMin = lsh * 60 + lsm;
    leMin = leh * 60 + lem;
    hasLunch = leMin > lsMin;
  }

  for (let m = startMin; m + duration <= endMin; m += duration) {
    const slotEnd = m + duration;
    const timeStr = `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
    const isLunch = hasLunch && m < leMin && slotEnd > lsMin;
    slots.push({ time: timeStr, isLunch });
  }
  return slots;
}

function getJsDayIndex(dateStr) {
  const d = new Date(dateStr + 'T00:00:00');
  const jsDay = d.getDay();
  return jsDay === 0 ? 6 : jsDay - 1;
}

export const UserAppointments = () => {
  const { user } = useAuth();
  const API_URL = process.env.REACT_APP_API_URL;

  const [appointments, setAppointments] = useState([]);
  const [busySlots, setBusySlots] = useState([]);
  const [settings, setSettings] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [showRequest, setShowRequest] = useState(false);
  const [form, setForm] = useState({ service: '', date: '', start_time: '', notes: '' });

  useEffect(() => { fetchAppointments(); }, []);
  useEffect(() => { fetchSlots(); }, [selectedDate]);

  const fetchAppointments = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/appointments`, { withCredentials: true });
      setAppointments(data);
    } catch (e) { /* empty */ }
  };

  const fetchSlots = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/appointments/available-slots?date=${selectedDate}`, { withCredentials: true });
      setBusySlots(data.busy_slots || []);
      if (data.settings) setSettings(data.settings);
    } catch (e) { /* empty */ }
  };

  const slotDuration = settings?.slot_duration || 30;
  const workingDays = settings?.working_days || [0, 1, 2, 3, 4, 5];
  const timeSlots = settings
    ? generateTimeSlots(
        settings.working_hours_start, settings.working_hours_end,
        settings.slot_duration,
        settings.lunch_break_start, settings.lunch_break_end
      )
    : [];

  const selectedDayIndex = getJsDayIndex(selectedDate);
  const isDayOff = !workingDays.includes(selectedDayIndex);

  const isSlotBusy = (time) => {
    const [h, m] = time.split(':').map(Number);
    const slotEnd = h * 60 + m + slotDuration;
    const slotEndStr = `${String(Math.floor(slotEnd / 60)).padStart(2, '0')}:${String(slotEnd % 60).padStart(2, '0')}`;
    return busySlots.some(busy => time < busy.end_time && slotEndStr > busy.start_time);
  };

  const handleRequest = async () => {
    if (!form.service.trim()) { toast.error('Talebinizi yazın'); return; }
    if (!form.start_time) { toast.error('Saat seçin'); return; }
    try {
      await axios.post(`${API_URL}/api/appointments/request`, {
        service: form.service.trim(),
        date: selectedDate,
        start_time: form.start_time,
        duration: slotDuration,
        notes: form.notes || '',
      }, { withCredentials: true });
      toast.success('Randevu talebiniz gönderildi! Onay bekleniyor.');
      setShowRequest(false);
      setForm({ service: '', date: '', start_time: '', notes: '' });
      fetchAppointments();
      fetchSlots();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Talep gönderilemedi');
    }
  };

  const handleCancelByClient = async (id) => {
    if (!window.confirm('Bu randevuyu iptal etmek istediğinizden emin misiniz?')) return;
    try {
      await axios.put(`${API_URL}/api/appointments/${id}/cancel-by-client`, {}, { withCredentials: true });
      toast.success('Randevunuz iptal edildi');
      fetchAppointments();
      fetchSlots();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'İptal edilemedi');
    }
  };

  const changeDate = (days) => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + days);
    setSelectedDate(d.toISOString().split('T')[0]);
  };

  const upcoming = appointments.filter(a => a.status === 'scheduled' || a.status === 'pending_approval');
  const past = appointments.filter(a => a.status === 'completed' || a.status === 'cancelled' || a.status === 'rejected');

  const workingDayNames = workingDays.map(i => DAY_NAMES_TR[i]).join(', ');
  const selectedDayName = DAY_NAMES_TR[selectedDayIndex];

  return (
    <PageLayout>
      
      
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-[#0F172A]" data-testid="my-appointments-title">Randevularım</h1>
              <p className="text-base text-[#595959] mt-2">Randevularınızı görüntüleyin ve yeni talep oluşturun</p>
            </div>
            <Button className="bg-[#2563EB] hover:bg-[#1E4ED8] text-white gap-2" onClick={() => setShowRequest(!showRequest)} data-testid="request-appointment-button">
              <Plus className="w-4 h-4" /> Randevu Talebi
            </Button>
          </div>

          {/* Working Hours Info Bar */}
          {settings && showRequest && (
            <div className="flex flex-wrap items-center gap-4 mb-4 p-3 bg-[#F0F7F4] border border-[#2563EB]/20 rounded-lg text-sm" data-testid="working-hours-info">
              <div className="flex items-center gap-1.5 text-[#2563EB]">
                <Clock className="w-4 h-4" />
                <span className="font-medium">{settings.working_hours_start} - {settings.working_hours_end}</span>
              </div>
              {settings.lunch_break_start && settings.lunch_break_end && (
                <div className="flex items-center gap-1.5 text-amber-700">
                  <Coffee className="w-4 h-4" />
                  <span>Öğle arası: {settings.lunch_break_start} - {settings.lunch_break_end}</span>
                </div>
              )}
              <div className="flex items-center gap-1.5 text-[#595959]">
                <CalendarDays className="w-4 h-4" />
                <span>{workingDayNames}</span>
              </div>
              <div className="text-[#595959]">
                Süre: <span className="font-medium">{slotDuration} dk</span>
              </div>
            </div>
          )}

          {showRequest && (
            <Card className="border-2 border-[#2563EB] mb-6" data-testid="request-form-card">
              <CardHeader><CardTitle className="text-lg text-[#2563EB]">Yeni Randevu Talebi</CardTitle></CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Talebiniz</Label>
                  <Input
                    value={form.service}
                    onChange={(e) => setForm({ ...form, service: e.target.value })}
                    placeholder="Örn: Saç kesimi, sakal tıraşı, fön çekim..."
                    data-testid="request-service-input"
                  />
                  <p className="text-xs text-[#595959] mt-1">Ne yaptırmak istediğinizi kısaca yazın</p>
                </div>

                <div>
                  <Label>Tarih Seçin</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Button variant="ghost" size="sm" onClick={() => changeDate(-1)} data-testid="prev-date-btn"><ChevronLeft className="w-4 h-4" /></Button>
                    <Input type="date" value={selectedDate} onChange={(e) => setSelectedDate(e.target.value)} className="w-48" data-testid="request-date-input" />
                    <Button variant="ghost" size="sm" onClick={() => changeDate(1)} data-testid="next-date-btn"><ChevronRight className="w-4 h-4" /></Button>
                    <span className="text-sm text-[#595959] ml-2" data-testid="selected-day-name">{selectedDayName}</span>
                  </div>
                </div>

                {isDayOff ? (
                  <div className="flex items-center gap-3 p-4 bg-amber-50 border border-amber-200 rounded-lg" data-testid="day-off-warning">
                    <AlertCircle className="w-5 h-5 text-amber-600 shrink-0" />
                    <div>
                      <p className="font-medium text-amber-800">Bu gün tatil</p>
                      <p className="text-sm text-amber-600">{selectedDayName} günü çalışma günü değil. Lütfen başka bir tarih seçin.</p>
                    </div>
                  </div>
                ) : (
                  <div>
                    <Label>Saat Seçin ({slotDuration} dakika)</Label>
                    <div className="grid grid-cols-5 sm:grid-cols-8 gap-2 mt-2" data-testid="time-slots-grid">
                      {timeSlots.map(({ time, isLunch }) => {
                        const busy = isSlotBusy(time);
                        const disabled = busy || isLunch;
                        const isSelected = form.start_time === time;
                        return (
                          <button
                            key={time}
                            disabled={disabled}
                            onClick={() => setForm({ ...form, start_time: time })}
                            className={`py-2.5 px-1 rounded-lg text-sm font-medium transition-all relative ${
                              isLunch
                                ? 'bg-amber-50 text-amber-400 cursor-not-allowed border border-amber-200'
                                : busy
                                  ? 'bg-red-50 text-red-400 cursor-not-allowed line-through border border-red-200'
                                  : isSelected
                                    ? 'bg-[#2563EB] text-white shadow-md scale-105'
                                    : 'bg-[#F0F7F4] text-[#2563EB] hover:bg-[#2563EB] hover:text-white border border-transparent hover:shadow-sm'
                            }`}
                            data-testid={`slot-${time.replace(':', '')}`}
                          >
                            {time}
                            {isLunch && <Coffee className="w-3 h-3 mx-auto mt-0.5 text-amber-400" />}
                          </button>
                        );
                      })}
                    </div>
                    {timeSlots.length === 0 && (
                      <p className="text-sm text-[#595959] text-center py-4">Çalışma saatleri belirlenmemiş</p>
                    )}
                    <div className="flex gap-4 mt-3 text-xs text-[#595959]">
                      <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-[#F0F7F4] border"></span> Müsait</span>
                      <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-50 border border-red-200"></span> Dolu</span>
                      <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-50 border border-amber-200"></span> Öğle Arası</span>
                    </div>
                  </div>
                )}

                <div>
                  <Label>Not (Opsiyonel)</Label>
                  <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Ek bilgi..." rows={2} data-testid="request-notes-input" />
                </div>

                <Button
                  onClick={handleRequest}
                  disabled={!form.service.trim() || !form.start_time || isDayOff}
                  className="w-full bg-[#2563EB] hover:bg-[#1E4ED8] text-white h-11"
                  data-testid="submit-request-button"
                >
                  Randevu Talebi Gönder
                </Button>
              </CardContent>
            </Card>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
            <Card className="border border-[#E5E5E5]"><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-[#0F172A]" data-testid="upcoming-count">{upcoming.length}</p><p className="text-sm text-[#595959]">Aktif / Bekleyen</p></CardContent></Card>
            <Card className="border border-[#E5E5E5]"><CardContent className="p-4 text-center"><p className="text-2xl font-bold text-[#595959]" data-testid="past-count">{past.length}</p><p className="text-sm text-[#595959]">Geçmiş</p></CardContent></Card>
          </div>

          <div className="bg-white border border-[#E5E5E5] rounded-xl" data-testid="user-appointments-table">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tarih</TableHead><TableHead>Saat</TableHead><TableHead>Talep</TableHead><TableHead>Durum</TableHead><TableHead className="text-right">İşlem</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {appointments.length === 0 ? (
                  <TableRow><TableCell colSpan={5} className="text-center py-8 text-[#595959]"><CalendarDays className="w-8 h-8 mx-auto mb-2 opacity-50" />Randevunuz bulunmuyor</TableCell></TableRow>
                ) : appointments.map((apt) => {
                  const status = statusLabels[apt.status] || statusLabels.scheduled;
                  const canCancel = apt.status === 'pending_approval' || apt.status === 'scheduled';
                  return (
                    <TableRow key={apt.id} data-testid={`user-apt-row-${apt.id}`}>
                      <TableCell className="font-medium">{apt.date}</TableCell>
                      <TableCell>{apt.start_time} - {apt.end_time}</TableCell>
                      <TableCell>{apt.service}{apt.notes ? <span className="block text-xs text-[#595959]">{apt.notes}</span> : null}</TableCell>
                      <TableCell><span className={`px-2 py-1 rounded-full text-xs font-medium ${status.color}`}>{status.text}</span></TableCell>
                      <TableCell className="text-right">
                        {canCancel && (
                          <Button variant="ghost" size="sm" onClick={() => handleCancelByClient(apt.id)} title="İptal Et" data-testid={`cancel-apt-${apt.id}`}>
                            <X className="w-4 h-4 text-red-500" />
                            <span className="text-xs text-red-500 ml-1">İptal</span>
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </div>
      
    </PageLayout>
  );
};
